import { useMemo, useState } from "react";
import { useMutation } from "@apollo/client";
import { notifications } from "@mantine/notifications";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { useForm } from "react-hook-form";
import { SendOrderRedoRequestDocument } from "/@/generated/graphql";
import {
  BankHoliday,
  downloadBase64File,
  getNextBusinessDay,
  isBusinessDay,
  isIntegratedCarrier,
  isPickupRequestAllowedForDoctor,
  type Order,
  ShippingType,
} from "@jasper/shared";

type UseOrderRedoRequestParams = {
  order: Order;
  userEmail: string;
  bankHolidays: BankHoliday[];
};

// Redo request: free text + optional pickup (date and clinic closing time).
export const useOrderRedoRequest = ({
  order,
  bankHolidays,
  userEmail,
}: UseOrderRedoRequestParams) => {
  const [redoRequestMessage, setRedoRequestMessage] = useState("");
  const [newDeliveryDate, setNewDeliveryDate] = useState<string | null>(null);
  const [clinicCloseTime, setClinicCloseTime] = useState<string>("18:00");
  const [isPickupRequested, setIsPickupRequested] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [sendOrderRedoRequest, { error }] = useMutation(
    SendOrderRedoRequestDocument
  );

  const dt = useMemo(() => new Date(), []);

  const todayDate = dt.toString();

  const nextBusinessDay = getNextBusinessDay(dt, bankHolidays);

  const defaultPickupDate = isBusinessDay(dt, bankHolidays)
    ? todayDate
    : nextBusinessDay?.date?.toString();

  const isPickupFromWebappEnabled =
    useFeatureFlagEnabled("PICKUP_FROM_WEBAPP") ?? false;

  const pickupCarrier = order?.user?.userGroup?.pickupCarrier;
  const canRequestPickup =
    isPickupFromWebappEnabled &&
    isPickupRequestAllowedForDoctor(order?.products ?? [], true) &&
    !!pickupCarrier &&
    isIntegratedCarrier(pickupCarrier, ShippingType.PICKUP, userEmail);

  const pickupDateOptions = [
    ...(isBusinessDay(new Date(), bankHolidays)
      ? [
          {
            label: "Aujourd'hui",
            value: todayDate,
          },
        ]
      : []),
    {
      label: nextBusinessDay.displayDayFr,
      value: nextBusinessDay?.date?.toString(),
    },
  ];

  const pickupForm = useForm({
    defaultValues: {
      date: defaultPickupDate,
    },
  });

  const onSubmit = async (data: { date: string }) => {
    try {
      setIsLoading(true);
      const req = await sendOrderRedoRequest({
        variables: {
          orderId: order.id,
          comment: redoRequestMessage,
          clinicClosingTime: isPickupRequested
            ? `${clinicCloseTime}:00`
            : undefined,
          scheduledDate: isPickupRequested ? data.date : undefined,
        },
      });
      if (!req || !req.data) {
        throw new Error(`Error on redo request - webapp: ${req} ${req?.data}`);
      }
      setNewDeliveryDate(
        req?.data?.redoOrder
          ? new Date(req.data.redoOrder.newDeliveryDate).toLocaleString(
              "fr-FR",
              {
                year: "numeric",
                month: "long",
                day: "numeric",
                weekday: "long",
              }
            )
          : "En cours de calcul. Elle vous sera communiquée prochainement"
      );
      if (req.data.redoOrder?.file) {
        downloadBase64File(
          req.data.redoOrder.file,
          `etiquette-de-ramassage-${order.orderReference}`,
          ".pdf"
        );
        notifications.show({
          color: "green",
          title: "Étiquette téléchargée",
          message: "",
        });
      }
      setIsLoading(false);
    } catch (e) {
      console.error(e);
      setIsLoading(false);
    }
  };

  const confirmSent = (onConfirmed: () => void) => {
    notifications.show({
      color: "green",
      title: "Votre demande a bien été envoyée",
      message: "",
    });
    onConfirmed();
  };

  return {
    pickupForm,
    submit: pickupForm.handleSubmit(onSubmit),
    confirmSent,
    setRedoRequestMessage,
    canRequestPickup,
    isPickupRequested,
    setIsPickupRequested,
    pickupDateOptions,
    clinicCloseTime,
    setClinicCloseTime,
    isLoading,
    error,
    newDeliveryDate,
  };
};

export type OrderRedoRequest = ReturnType<typeof useOrderRedoRequest>;
